import { useState, useEffect, useCallback } from 'react'
import { Folder, FolderOpen, ArrowUp, HardDrive, Loader2 } from 'lucide-react'
import { IconLabel } from './app-icon'
import { Modal } from './modal'
import { Button } from './button'
import { Input } from './input'
import { filesystemApi, type FsEntry } from '@/lib/api/misc'

interface FolderBrowserProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (path: string) => void
  title?: string
}

export function FolderBrowser({ isOpen, onClose, onSelect, title = 'Выберите папку' }: FolderBrowserProps) {
  const [currentPath, setCurrentPath] = useState('')
  const [parentPath, setParentPath] = useState<string | null>(null)
  const [items, setItems] = useState<FsEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [manualPath, setManualPath] = useState('')

  const browse = useCallback(async (path?: string) => {
    setLoading(true)
    setError('')
    try {
      const res = await filesystemApi.browse(path)
      const data = res.data
      setCurrentPath(data.path)
      setParentPath(data.parent)
      setItems(data.items)
      setManualPath(data.path)
    } catch {
      setError('Не удалось прочитать каталог')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (isOpen) {
      browse()
    }
  }, [isOpen, browse])

  const handleNavigate = (entry: FsEntry) => {
    if (entry.is_dir) {
      browse(entry.path)
    }
  }

  const handleGoUp = () => {
    if (parentPath !== null) {
      browse(parentPath)
    } else {
      // Go to root (drives list on Windows)
      browse()
    }
  }

  const handleGoToPath = () => {
    const p = manualPath.trim()
    if (p) {
      browse(p)
    }
  }

  const handleConfirm = () => {
    if (currentPath) {
      onSelect(currentPath)
      onClose()
    }
  }

  // Only show directories for selection
  const dirs = items.filter(i => i.is_dir)

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-xl">
      <Modal.Header onClose={onClose}>
        <IconLabel icon="folder">{title}</IconLabel>
      </Modal.Header>
      <Modal.Body className="p-0">
        {/* Address bar */}
        <div className="flex items-center gap-2 px-4 py-3 border-b bg-gray-50">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleGoUp}
            disabled={!currentPath}
            title="Вверх"
          >
            <ArrowUp className="w-4 h-4" />
          </Button>
          <Input
            className="flex-1 text-sm font-mono"
            value={manualPath}
            onChange={e => setManualPath(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleGoToPath() }}
            placeholder="Введите путь..."
          />
          <Button variant="secondary" size="sm" onClick={handleGoToPath}>
            Перейти
          </Button>
        </div>

        {/* Current path display */}
        {currentPath && (
          <div className="px-4 py-2 text-xs text-gray-500 bg-gray-50 border-b">
            Текущий каталог: <span className="font-mono font-medium text-gray-700">{currentPath}</span>
          </div>
        )}

        {/* File list */}
        <div className="min-h-[300px] max-h-[400px] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              Загрузка...
            </div>
          ) : error ? (
            <div className="px-4 py-8 text-center text-red-500 text-sm">{error}</div>
          ) : dirs.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-400 text-sm">
              {currentPath ? 'Нет подпапок в этом каталоге' : 'Нет доступных дисков'}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {dirs.map(entry => (
                <button
                  key={entry.path}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-blue-50 transition-colors group"
                  onDoubleClick={() => handleNavigate(entry)}
                  onClick={() => {
                    setManualPath(entry.path)
                  }}
                >
                  {!currentPath ? (
                    <HardDrive className="w-5 h-5 text-blue-500 flex-shrink-0" />
                  ) : (
                    <Folder className="w-5 h-5 text-yellow-500 flex-shrink-0 group-hover:hidden" />
                  )}
                  {currentPath && (
                    <FolderOpen className="w-5 h-5 text-yellow-600 flex-shrink-0 hidden group-hover:block" />
                  )}
                  <span className="text-sm text-gray-800 truncate">{entry.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="ghost" onClick={onClose}>Отмена</Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={!currentPath}
        >
          Выбрать эту папку
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
